import React, { useEffect, useMemo, useState } from 'react';
import { Input, Typography } from '@attentive-platform/stem-ui';

import { cardById, versionOn } from '../../CustomerScopeForm/tat';
import { fetchScopeCatalog } from '../api';
import { normalizeTatCardsPayload } from './normalizeTatCards';
import { TatConsoleStyles as S } from './styles';

const fmtApplies = iso => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
      new Date(`${iso}T12:00:00`)
    );
  } catch {
    return iso;
  }
};

const filterRows = (rows, q) => {
  const needle = (q || '').trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(r => (r.trade_name || '').toLowerCase().includes(needle));
};

/**
 * Load turnaround cards from get_scopes (API 9).
 * Falcon has not registered /api/dashboard/tat_cards/ yet — calling it 404s the tab.
 * When that route ships, prefer fetchTatCards (API 13) for internal_days + version history.
 */
const loadTurnaroundCatalog = async ({ search, signal } = {}) => {
  const catalog = await fetchScopeCatalog({ signal });
  if (catalog.error || !catalog.data) {
    return {
      rows: [],
      error:
        typeof catalog.error === 'string'
          ? catalog.error
          : catalog.error?.detail || 'Failed to load turnaround cards'
    };
  }
  return {
    rows: filterRows(normalizeTatCardsPayload(catalog.data), search),
    error: null
  };
};

const TradeCardsBlock = ({ tradeName, cfg }) => {
  const [expanded, setExpanded] = useState({});
  const [versionPick, setVersionPick] = useState({});

  if (!cfg?.cards?.length) return null;

  return (
    <S.TradeBlock>
      <S.TradeHead>
        <S.TradeHeadLeft>
          <S.TradeName>{tradeName}</S.TradeName>
          <S.TradeMeta>
            {cfg.presets.length} preset{cfg.presets.length === 1 ? '' : 's'} · {cfg.cards.length} card
            {cfg.cards.length === 1 ? '' : 's'}
          </S.TradeMeta>
        </S.TradeHeadLeft>
        {cfg.customRanges ? (
          <S.OwnRanges>
            own ranges · {cfg.ranges.length} columns
          </S.OwnRanges>
        ) : null}
      </S.TradeHead>
      <S.TradeBody>
        <S.PresetsCol>
          <S.Eyebrow>Presets, lightest first</S.Eyebrow>
          {cfg.presets.length === 0 ? (
            <S.PresetDesc>No presets on this trade — cards only.</S.PresetDesc>
          ) : (
            cfg.presets.map(pr => {
              const n = (pr.addsItems || []).length;
              const m = (pr.addsAnswers || []).length;
              const key = pr.id;
              const ex = !!expanded[key];
              const card = cardById(cfg, pr.cardId);
              const hasAdds = n + m > 0;
              const addsLabel =
                `adds ${n ? `${n} item${n === 1 ? '' : 's'}` : ''}${n && m ? ', ' : ''}${
                  m ? `${m} answer${m === 1 ? '' : 's'}` : ''
                }${ex ? ' ▾' : ' ▸'}`;
              return (
                <S.PresetRow key={pr.id}>
                  <S.Rung>{pr.rung}</S.Rung>
                  <S.PresetBody>
                    <S.PresetNameRow>
                      <S.PresetName>{pr.name}</S.PresetName>
                      <S.PresetCard>→ {card?.name || '—'}</S.PresetCard>
                    </S.PresetNameRow>
                    {pr.desc ? <S.PresetDesc>{pr.desc}</S.PresetDesc> : null}
                    {hasAdds ? (
                      <>
                        <S.AddsBtn
                          type='button'
                          aria-expanded={ex}
                          onClick={() => setExpanded(s => ({ ...s, [key]: !s[key] }))}>
                          {addsLabel}
                        </S.AddsBtn>
                        {ex ? (
                          <S.AddsNames>
                            {[...(pr.addsItems || []), ...(pr.addsAnswers || [])].join(' · ')}
                          </S.AddsNames>
                        ) : null}
                      </>
                    ) : null}
                  </S.PresetBody>
                </S.PresetRow>
              );
            })
          )}
        </S.PresetsCol>
        <S.CardsCol>
          <S.TableHead $cols={cfg.ranges.length}>
            <S.TableHeadLabel>Card</S.TableHeadLabel>
            <span />
            {cfg.ranges.map(l => (
              <S.TableRange key={l}>{l}</S.TableRange>
            ))}
            <S.TableHeadLabel>Applies from</S.TableHeadLabel>
          </S.TableHead>
          {cfg.cards.map(c => {
            const sorted = [...(c.versions || [])].sort((a, b) =>
              (a.appliesFrom || '') < (b.appliesFrom || '') ? 1 : -1
            );
            const vi = versionPick[c.id] ?? 0;
            const v = sorted[vi] || sorted[0] || versionOn(c);
            const readOnlyNote = sorted.length > 1 && vi > 0;
            return (
              <React.Fragment key={c.id}>
                <S.CardBlock $cols={cfg.ranges.length}>
                  <S.CardName>
                    {c.name}
                    <div style={{ fontSize: 11, color: '#9E9E9E', fontWeight: 400 }}>
                      tier {c.tier} of {c.of}
                    </div>
                  </S.CardName>
                  <S.RowLabel>Customer-facing</S.RowLabel>
                  {(v?.customer || []).map((d, i) => (
                    <S.DayCell
                      key={`c-${i}`}
                      $quote={d == null}
                      $bg={d == null ? '#FFF8EC' : '#F5F7FF'}>
                      {d == null ? 'Quote' : d}
                    </S.DayCell>
                  ))}
                  <div>
                    {sorted.length > 1 ? (
                      <>
                        <S.VersionSelect
                          value={String(vi)}
                          onChange={e =>
                            setVersionPick(s => ({ ...s, [c.id]: parseInt(e.target.value, 10) }))
                          }
                          aria-label={`${c.name} version`}>
                          {sorted.map((vv, i) => (
                            <option key={vv.appliesFrom} value={String(i)}>
                              from {fmtApplies(vv.appliesFrom)}
                              {i === 0 ? ' · newest' : ''}
                            </option>
                          ))}
                        </S.VersionSelect>
                        {readOnlyNote ? <S.ReadOnlyNote>older version · read-only</S.ReadOnlyNote> : null}
                      </>
                    ) : (
                      <S.Applies>from {fmtApplies(v?.appliesFrom)}</S.Applies>
                    )}
                  </div>
                </S.CardBlock>
                <S.CardBlock $cols={cfg.ranges.length} style={{ borderTop: 'none', paddingTop: 0 }}>
                  <span />
                  <S.RowLabel $muted>Internal</S.RowLabel>
                  {(v?.internal || []).map((d, i) => (
                    <S.DayCell key={`i-${i}`} $quote={d == null} $internal $bg='#FAFAFA'>
                      {d == null ? '—' : d}
                    </S.DayCell>
                  ))}
                  <span />
                </S.CardBlock>
              </React.Fragment>
            );
          })}
        </S.CardsCol>
      </S.TradeBody>
    </S.TradeBlock>
  );
};

const TurnaroundCardsTab = () => {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    loadTurnaroundCatalog({ search: debounced || undefined, signal: controller.signal }).then(
      ({ rows: next, error: err }) => {
        if (cancelled) return;
        if (err) {
          setError(err);
          setRows([]);
        } else {
          setRows(next);
        }
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [debounced]);

  const visible = useMemo(() => rows, [rows]);

  return (
    <S.CardsWrap>
      <S.CardsHeader>
        <S.CardsTitleBlock>
          <S.CardsTitle>Turnaround cards</S.CardsTitle>
          <S.CardsSub>
            Every trade&apos;s preset ladder and card set. Read-only — each card carries two rows of business days: the{' '}
            <strong style={{ fontWeight: 600 }}>customer-facing</strong> promise on top, the{' '}
            <strong style={{ fontWeight: 600 }}>internal</strong> target beneath it.
          </S.CardsSub>
        </S.CardsTitleBlock>
        <div style={{ width: 300 }}>
          <Input
            size='large'
            width='100%'
            placeholder='Search trades…'
            value={query}
            onChange={e => setQuery(e?.target ? e.target.value : e || '')}
          />
        </div>
      </S.CardsHeader>
      <S.CardsCount>
        {visible.length} trade{visible.length === 1 ? '' : 's'} with a card
      </S.CardsCount>
      {loading ? (
        <Typography variant='body2' color='#666666'>
          Loading turnaround cards…
        </Typography>
      ) : error ? (
        <S.Empty>{error}</S.Empty>
      ) : visible.length === 0 ? (
        <S.Empty>No trades match this search.</S.Empty>
      ) : (
        visible.map(r => (
          <TradeCardsBlock key={r.cfg.id} tradeName={r.trade_name} cfg={r.cfg} />
        ))
      )}
    </S.CardsWrap>
  );
};

export default TurnaroundCardsTab;
